import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, Clock3, FileCheck2, OctagonAlert } from "lucide-react";
import { Card } from "@shared/ui/containers/Card";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { Dropdown } from "@shared/ui/primitives/Dropdown";
import {
  fetchAvailableSurveys,
  fetchMyResponses,
  type MyResponse,
  type SurveyView,
} from "./api";
import { formTypeLabel } from "./routeContext";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

type Tab = "all" | "open" | "inProgress" | "completed" | "expired";

interface OpenCard {
  kind: "open";
  survey_template_id: string;
  name: string;
  description?: string | null;
  flow_type: string;
  deadline_at?: string | null;
}

interface ResponseCard {
  kind: "inProgress" | "completed" | "expired";
  survey_instance_id: string;
  flow_instance_id: string;
  survey_template_id: string;
  name: string;
  description?: string | null;
  flow_type: string;
  deadline_at?: string | null;
  submitted_at?: string | null;
  created_at: string;
}

const TABS: Array<{ value: Tab; label: string }> = [
  { value: "all", label: "Show all statuses" },
  { value: "open", label: "Open" },
  { value: "inProgress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "expired", label: "Expired" },
];

export function SurveysList(): JSX.Element {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("all");

  const availableQuery = useQuery({
    queryKey: ["surveys-available"],
    queryFn: fetchAvailableSurveys,
    refetchOnMount: "always",
  });
  const responsesQuery = useQuery({
    queryKey: ["surveys-my"],
    queryFn: fetchMyResponses,
    refetchOnMount: "always",
  });

  const buckets = useMemo(
    () => bucketCards(availableQuery.data ?? [], responsesQuery.data ?? []),
    [availableQuery.data, responsesQuery.data],
  );

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow="Community"
        title="Surveys"
        subtitle="Complete surveys and eForms shared with you, and review your past submissions."
        backTo="/app/home"
      />

      <div className="px-5 pt-5 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
        <Card noGrain className="mb-4 p-4">
          <p className="eyebrow mb-2.5">Filter by status</p>
          <Dropdown
            value={tab}
            onChange={(event) => setTab(event.target.value as Tab)}
            aria-label="Filter surveys by status"
          >
            {TABS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Dropdown>
        </Card>

        {availableQuery.isLoading || responsesQuery.isLoading ? (
          <SkeletonList />
        ) : availableQuery.isError || responsesQuery.isError ? (
          <ErrorState
            onRetry={() => {
              void availableQuery.refetch();
              void responsesQuery.refetch();
            }}
          />
        ) : (
          renderTab(tab, buckets, navigate)
        )}
      </div>
    </div>
  );
}

function renderTab(
  tab: Tab,
  buckets: BucketedCards,
  navigate: ReturnType<typeof useNavigate>,
): JSX.Element {
  switch (tab) {
    case "all": {
      const cards = [
        ...buckets.open,
        ...buckets.inProgress,
        ...buckets.completed,
        ...buckets.expired,
      ];
      if (cards.length === 0) {
        return (
          <EmptyState
            icon={<ClipboardList className="h-7 w-7" strokeWidth={1.5} />}
            title="No surveys available right now."
            description="When surveys or eForms are shared with you, they will appear here."
          />
        );
      }
      return (
        <div className="flex flex-col gap-3">
          {cards.map((card) =>
            card.kind === "open" ? (
              <OpenSurveyCard
                key={card.survey_template_id}
                card={card}
                onOpen={() => navigate(`/app/surveys/${card.survey_template_id}`)}
              />
            ) : (
              <ResponseSurveyCard
                key={card.survey_instance_id}
                card={card}
                statusLabel={
                  card.kind === "completed"
                    ? "Submitted"
                    : card.kind === "expired"
                      ? "Expired"
                      : "In progress"
                }
                statusTone={
                  card.kind === "completed"
                    ? "bg-success/15 text-success"
                    : card.kind === "expired"
                      ? "bg-danger/10 text-danger"
                      : "bg-warning/15 text-warning"
                }
                iconTone={
                  card.kind === "completed"
                    ? "bg-success/15 text-success"
                    : card.kind === "expired"
                      ? "bg-muted text-ink-muted"
                      : "bg-warning/15 text-warning"
                }
                onOpen={
                  card.kind === "completed"
                    ? () =>
                        navigate(`/app/surveys/${card.survey_template_id}/response/${card.survey_instance_id}`)
                    : card.kind === "inProgress"
                      ? () =>
                          navigate(`/app/surveys/${card.survey_template_id}/fill/${card.flow_instance_id}`)
                      : undefined
                }
              />
            ),
          )}
        </div>
      );
    }

    case "open":
      if (buckets.open.length === 0) {
        return (
          <EmptyState
            icon={<ClipboardList className="h-7 w-7" strokeWidth={1.5} />}
            title="No open surveys right now."
            description="When something new is shared with you, it will appear here."
          />
        );
      }
      return (
        <div className="flex flex-col gap-3">
          {buckets.open.map((card) => (
            <OpenSurveyCard
              key={card.survey_template_id}
              card={card}
              onOpen={() => navigate(`/app/surveys/${card.survey_template_id}`)}
            />
          ))}
        </div>
      );

    case "inProgress":
      if (buckets.inProgress.length === 0) {
        return (
          <EmptyState
            icon={<Clock3 className="h-7 w-7" strokeWidth={1.5} />}
            title="Nothing in progress."
            description="Surveys you've started but not yet submitted will show up here."
          />
        );
      }
      return (
        <div className="flex flex-col gap-3">
          {buckets.inProgress.map((card) => (
            <ResponseSurveyCard
              key={card.survey_instance_id}
              card={card}
              statusLabel="In progress"
              statusTone="bg-warning/15 text-warning"
              iconTone="bg-warning/15 text-warning"
              onOpen={() =>
                navigate(`/app/surveys/${card.survey_template_id}/fill/${card.flow_instance_id}`)
              }
            />
          ))}
        </div>
      );

    case "completed":
      if (buckets.completed.length === 0) {
        return (
          <EmptyState
            icon={<FileCheck2 className="h-7 w-7" strokeWidth={1.5} />}
            title="No submissions yet."
            description="Once you submit a survey, you can come back here to review your answers."
          />
        );
      }
      return (
        <div className="flex flex-col gap-3">
          {buckets.completed.map((card) => (
            <ResponseSurveyCard
              key={card.survey_instance_id}
              card={card}
              statusLabel="Submitted"
              statusTone="bg-success/15 text-success"
              iconTone="bg-success/15 text-success"
              onOpen={() =>
                navigate(`/app/surveys/${card.survey_template_id}/response/${card.survey_instance_id}`)
              }
            />
          ))}
        </div>
      );

    case "expired":
      if (buckets.expired.length === 0) {
        return (
          <EmptyState
            icon={<OctagonAlert className="h-7 w-7" strokeWidth={1.5} />}
            title="No expired surveys."
            description="Surveys you started but didn't submit before the deadline will appear here."
          />
        );
      }
      return (
        <div className="flex flex-col gap-3">
          {buckets.expired.map((card) => (
            <ResponseSurveyCard
              key={card.survey_instance_id}
              card={card}
              statusLabel="Expired"
              statusTone="bg-danger/10 text-danger"
              iconTone="bg-muted text-ink-muted"
              onOpen={undefined}
            />
          ))}
        </div>
      );
  }
}

function SkeletonList(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
    </div>
  );
}

function OpenSurveyCard({
  card,
  onOpen,
}: {
  card: OpenCard;
  onOpen: () => void;
}): JSX.Element {
  const formType = formTypeLabel(card.flow_type);
  const daysRemaining = daysUntil(card.deadline_at);
  const deadlineSoon = daysRemaining !== null && daysRemaining <= 3;

  return (
    <Card
      noGrain
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="cursor-pointer p-4 transition-transform duration-fast active:scale-[0.99]"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
          <ClipboardList className="h-4.5 w-4.5" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-pill bg-primary-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-primary-700">
              {formType}
            </span>
            <span className="inline-flex rounded-pill bg-success/15 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-success">
              Open
            </span>
          </div>
          <p className="mt-2 font-display text-headline text-ink">{card.name}</p>
          {card.description ? (
            <p className="mt-1 text-body-sm text-ink-secondary line-clamp-2">
              {card.description}
            </p>
          ) : null}
          {card.deadline_at ? (
            <p className={`mt-3 text-caption ${deadlineSoon ? "text-warning" : "text-ink-muted"}`}>
              {deadlineSoon && daysRemaining !== null
                ? `Closes in ${daysRemaining} day${daysRemaining === 1 ? "" : "s"} (${DATE_FMT.format(new Date(card.deadline_at))})`
                : `Closes ${DATE_FMT.format(new Date(card.deadline_at))}`}
            </p>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function ResponseSurveyCard({
  card,
  statusLabel,
  statusTone,
  iconTone,
  onOpen,
}: {
  card: ResponseCard;
  statusLabel: string;
  statusTone: string;
  iconTone: string;
  onOpen?: () => void;
}): JSX.Element {
  const formType = formTypeLabel(card.flow_type);
  const clickable = typeof onOpen === "function";

  return (
    <Card
      noGrain
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      className={`p-4 ${clickable ? "cursor-pointer transition-transform duration-fast active:scale-[0.99]" : "opacity-75"}`}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconTone}`}>
          {card.kind === "completed" ? (
            <FileCheck2 className="h-4.5 w-4.5" strokeWidth={1.5} />
          ) : (
            <Clock3 className="h-4.5 w-4.5" strokeWidth={1.5} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-pill bg-muted px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-ink-secondary">
              {formType}
            </span>
            <span className={`inline-flex rounded-pill px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] ${statusTone}`}>
              {statusLabel}
            </span>
          </div>
          <p className="mt-2 font-display text-headline text-ink">{card.name}</p>
          {card.description ? (
            <p className="mt-1 text-body-sm text-ink-secondary line-clamp-2">
              {card.description}
            </p>
          ) : null}
          <p className="mt-3 text-caption text-ink-muted">
            {card.kind === "completed" && card.submitted_at
              ? `Submitted ${DATE_FMT.format(new Date(card.submitted_at))}`
              : card.kind === "expired" && card.deadline_at
                ? `Closed ${DATE_FMT.format(new Date(card.deadline_at))}`
                  : card.deadline_at
                    ? `Due ${DATE_FMT.format(new Date(card.deadline_at))}`
                    : `Started ${DATE_FMT.format(new Date(card.created_at))}`}
          </p>
        </div>
      </div>
    </Card>
  );
}

interface BucketedCards {
  open: OpenCard[];
  inProgress: ResponseCard[];
  completed: ResponseCard[];
  expired: ResponseCard[];
}

function bucketCards(available: SurveyView[], mine: MyResponse[]): BucketedCards {
  const now = Date.now();
  const myTemplateIds = new Set(mine.map((response) => response.survey_template_id));

  const open: OpenCard[] = available
    .filter((survey) => !myTemplateIds.has(survey.survey_template_id))
    .map((survey) => ({
      kind: "open",
      survey_template_id: survey.survey_template_id,
      name: survey.name,
      description: survey.description,
      flow_type: survey.flow_type,
      deadline_at: survey.deadline_at,
    }));

  const inProgress: ResponseCard[] = [];
  const completed: ResponseCard[] = [];
  const expired: ResponseCard[] = [];

  mine.forEach((response) => {
    const base: Omit<ResponseCard, "kind"> = {
      survey_instance_id: response.survey_instance_id,
      flow_instance_id: response.flow_instance_id,
      survey_template_id: response.survey_template_id,
      name: response.name,
      description: response.description,
      flow_type: response.flow_type,
      deadline_at: response.deadline_at,
      submitted_at: response.submitted_at,
      created_at: response.created_at,
    };

    if (response.submitted_at) {
      completed.push({ ...base, kind: "completed" });
      return;
    }

    if (response.deadline_at && new Date(response.deadline_at).getTime() < now) {
      expired.push({ ...base, kind: "expired" });
      return;
    }

    inProgress.push({ ...base, kind: "inProgress" });
  });

  return { open, inProgress, completed, expired };
}

function daysUntil(date: string | null | undefined): number | null {
  if (!date) return null;
  const ms = new Date(date).getTime() - Date.now();
  if (ms < 0) return null;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
